import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, limit, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getStorage } from 'firebase/storage';
import { UserContext } from '../../context/UserContext';
import { TourContext } from '../../context/TourContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './EditProfile.module.css';
import { createBandNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faCamera, 
  faExclamationTriangle, 
  faSearch, 
  faTimes, 
  faCheckCircle, 
  faTimesCircle, 
  faInfoCircle, 
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faTwitter, 
  faFacebook, 
  faYoutube, 
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import defaultProfilePic from '../../assets/Images/defaultUserProfilePicture.png';
import defaultCoverPhoto from '../../assets/Images/defaultUserCoverPicture.png';
import spotifyLogo from '../../assets/Images/spotifyicon.png';
import appleMusicLogo from '../../assets/Images/applemusicicon.png';
import ImageCropper from '../common/ImageCropper';
import { uploadProfileImage, deleteOldImage } from '../../utils/ImageUtils';
import EndorsementsSection from '../ProfileSections/EndorsementsSection';
import AssociatedActsSection from '../ProfileSections/AssociatedActsSection';
import ManagedBySection from '../ProfileSections/managedBySection';

const EditProfile = () => {
  const { userData, setUserData } = useContext(UserContext);
  const { tours, updateTourData } = useContext(TourContext);
  const [isLoading, setIsLoading] = useState(false);
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [userName, setUserName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [showUsernameDialog, setShowUsernameDialog] = useState(false);
  const [associatedActs, setAssociatedActs] = useState([]);
  const [managedBy, setManagedBy] = useState([]);


  // For profile photos
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [profileCoverPictureUrl, setProfileCoverPictureUrl] = useState('');
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const profilePictureRef = useRef(null);
  const coverPhotoRef = useRef(null);
  const [showProfileCropper, setShowProfileCropper] = useState(false);
const [showCoverCropper, setShowCoverCropper] = useState(false);
const [selectedImage, setSelectedImage] = useState(null);
const [startedLearningYear, setStartedLearningYear] = useState('');
const [startedProfessionalYear, setStartedProfessionalYear] = useState('');
const [overallExperienceYears, setOverallExperienceYears] = useState(null);
const [professionalExperienceYears, setProfessionalExperienceYears] = useState(null);
  
  // For social links
  const [socialLinks, setSocialLinks] = useState({
    instagramUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    youtubeUrl: '',
    linkedInUrl: '',
    spotifyUrl: '',
    appleMusicUrl: ''
  });
  
  // For endorsements
  const [showBrandSearchModal, setShowBrandSearchModal] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [brandSearchResults, setBrandSearchResults] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  
  // For alerts
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: '',
    message: '',
    type: '', // 'success', 'error', 'warning'
    onConfirm: null,
    showCancel: false
  });

  useEffect(() => {
    if (userData) {
      // Set basic user data
      setFullName(userData.fullName || '');
      setBio(userData.bio || '');
      setUserName(userData.userName || '');
      setCity(userData.city || '');
      setState(userData.state || '');
      setCountry(userData.country || '');
      setStartedLearningYear(userData.startedLearningYear || '');
    setStartedProfessionalYear(userData.startedProfessionalYear || '');
    setOverallExperienceYears(userData.overallExperienceYears || null);
    setProfessionalExperienceYears(userData.professionalExperienceYears || null);
      setProfilePictureUrl(userData.profilePictureUrl || '');
      setProfileCoverPictureUrl(userData.profileCoverPictureUrl || '');
      setAssociatedActs(userData.associatedActs || []);
      setManagedBy(userData.managedBy || []);
      // Set social links
      setSocialLinks({
        instagramUrl: userData.socialLinks?.instagramUrl || '',
        twitterUrl: userData.socialLinks?.twitterUrl || '',
        facebookUrl: userData.socialLinks?.facebookUrl || '',
        youtubeUrl: userData.socialLinks?.youtubeUrl || '',
        linkedInUrl: userData.socialLinks?.linkedInUrl || '',
        spotifyUrl: userData.socialLinks?.spotifyUrl || '',
        appleMusicUrl: userData.socialLinks?.appleMusicUrl || ''
      });
      
      // Set endorsements
      if (userData.endorsedBrands && Array.isArray(userData.endorsedBrands)) {
        setSelectedBrands(userData.endorsedBrands);
      }
    }
  }, [userData]);

  // Function to generate year options from 1950 to current year
const generateYearOptions = useCallback(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 1950; year--) {
    years.push({ value: year, label: year.toString() });
  }
  return years;
}, []);

// Function to get the appropriate verb based on subType
const getActivityVerb = useCallback((subType) => {
  const verbMap = {
    // Music
    "Vocalist": "singing",
    "DJ": "DJing",
    "Drummer": "drumming",
    "Guitarist": "playing guitar",
    "Bass Guitarist": "playing bass guitar",
    "Keyboardist": "playing keyboard",
    "Violinist": "playing violin",
    "Cellist": "playing cello",
    "Flutist": "playing flute",
    "Trumpeter": "playing trumpet",
    "Saxophonist": "playing saxophone",
    "Percussionist": "playing percussion",
    "Tabla Player": "playing tabla",
    "Sitar Player": "playing sitar",
    "Santoor Player": "playing santoor",
    "Sarod Player": "playing sarod",
    "Veena Player": "playing veena",
    "Dhol Player": "playing dhol",
    "Dholak Player": "playing dholak",
    "Ghatam Player": "playing ghatam",
    "Harmonium Player": "playing harmonium",
    "Mridangam Player": "playing mridangam",
    "Shehnai Player": "playing shehnai",
    "Rapper": "rapping",
    "Music Producer": "producing music",
    
    // Crew
    "FOH Engineer": "engineering live sound",
    "Light Engineer": "designing lighting",
    "IEM Engineer": "working with in-ear monitoring",
    "Monitor Engineer": "engineering stage monitors",
    "Stage Manager": "managing stages",
    "Drum Tech": "teching drums",
    "Guitar Tech": "teching guitars",
    "Keyboard Tech": "teching keyboards",
    "Backline Technician": "working with backline",
    "Lighting Technician": "working with lighting",
    "Rigger": "rigging",
    "Videographer": "videography",
    "Photographer": "photography",
    "Merchandiser": "merchandising",
    "Dresser": "styling artists",
    "Makeup Artist": "doing makeup",
    "Security Personnel": "working in security",
    "Road Manager": "road managing",
    "Tour Bus Driver": "driving tour buses",
    "Stage Tech": "working as stage tech",
    
    // Managers
    "Tour Manager": "tour managing",
    "Artist Manager": "artist managing",
    
    // Comedy
    "Comedy Writer": "writing comedy",
    "Improv Comedian": "doing improv comedy",
    "Sketch Artist": "creating sketches",
    "Stand-Up Comedian": "doing stand-up",
    
    // Default
    "default": "working in this field"
  };
  
  return verbMap[subType] || verbMap["default"];
}, []);

// Effect to calculate experience years when year fields change
useEffect(() => {
  const currentYear = new Date().getFullYear();
  
  if (startedLearningYear) {
    const overallYears = currentYear - parseInt(startedLearningYear);
    setOverallExperienceYears(overallYears >= 0 ? overallYears : null);
  } else {
    setOverallExperienceYears(null);
  }
  
  if (startedProfessionalYear) {
    const professionalYears = currentYear - parseInt(startedProfessionalYear);
    setProfessionalExperienceYears(professionalYears >= 0 ? professionalYears : null);
  } else {
    setProfessionalExperienceYears(null);
  }
}, [startedLearningYear, startedProfessionalYear]);

  const handleProfilePicChange = (e) => {
    if (e.target.files?.[0]) {
      setSelectedImage({
        file: e.target.files[0],
        preview: URL.createObjectURL(e.target.files[0])
      });
      setShowProfileCropper(true);
    }
  };
  
  const handleCoverPhotoChange = (e) => {
    if (e.target.files?.[0]) {
      setSelectedImage({
        file: e.target.files[0],
        preview: URL.createObjectURL(e.target.files[0])
      });
      setShowCoverCropper(true);
    }
  };
  
  const handleCropComplete = async (croppedPixels) => {
    try {
      setIsLoading(true);
      const isProfile = showProfileCropper;
      const imageFile = selectedImage.file;
  
      // Upload the cropped image
      const imageUrl = await uploadProfileImage(
        imageFile,
        croppedPixels,
        userData.userId,
        isProfile
      );
  
      // Update state with new image URL
      if (isProfile) {
        setProfilePictureUrl(imageUrl);
        // Delete old profile picture if it exists
        if (userData.profilePictureUrl && userData.profilePictureUrl !== defaultProfilePic) {
          await deleteOldImage(userData.profilePictureUrl);
        }
        setShowProfileCropper(false);
      } else {
        setProfileCoverPictureUrl(imageUrl);
        // Delete old cover photo if it exists
        if (userData.profileCoverPictureUrl && userData.profileCoverPictureUrl !== defaultCoverPhoto) {
          await deleteOldImage(userData.profileCoverPictureUrl);
        }
        setShowCoverCropper(false);
      }
  
      // Update user document with new image URL
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
      await updateDoc(userRef, {
        [isProfile ? 'profilePictureUrl' : 'profileCoverPictureUrl']: imageUrl
      });

       // Update context
    setUserData(prev => ({
      ...prev,
      [isProfile ? 'profilePictureUrl' : 'profileCoverPictureUrl']: imageUrl
    }));

    // Show success message
    showCustomAlert({
      title: 'Success',
      message: `${isProfile ? 'Profile picture' : 'Cover photo'} updated successfully`,
      type: 'success'
    });
  } catch (error) {
    console.error('Error processing image:', error);
    showCustomAlert({
      title: 'Error',
      message: 'Failed to process image. Please try again.',
      type: 'error'
    });
  } finally {
    setIsLoading(false);
    setSelectedImage(null);
  }
};

  const showCustomAlert = (config) => {
    setAlertConfig(config);
    setShowAlert(true);
  };

  const isValidUsername = (username) => {
    const usernameRegex = /^[a-z0-9_]+$/;
    return usernameRegex.test(username);
  };

  // Username availability check
  const checkUsernameAvailability = async (username) => {
    const firestore = getFirestoreInstance();
    const usersRef = collection(firestore, 'users');
    const q = query(
      usersRef,
      where('userName', '==', username),
      limit(1)
    );
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.empty;
    } catch (error) {
      console.error('Error checking username availability:', error);
      return false;
    }
  };

  // Update username function
  const handleUpdateUsername = async () => {
    if (!isValidUsername(userName)) {
      showCustomAlert({
        title: 'Invalid Username',
        message: 'Username can only contain lowercase letters, numbers, and "_". No spaces or "@" symbol allowed.',
        type: 'error'
      });
      return;
    }

    if (userName === userData.userName) {
      showCustomAlert({
        title: 'No Change',
        message: 'The new username is the same as the current one.',
        type: 'warning'
      });
      return;
    }

    setIsLoading(true);
    console.log('Starting username update');
    
    try {
      const isAvailable = await checkUsernameAvailability(userName);
      if (!isAvailable) {
        showCustomAlert({
          title: 'Username Taken',
          message: 'This username is already in use. Please choose a different one.',
          type: 'error'
        });
        setIsLoading(false);
        return;
      }

      const newProfileUrl = `https://ontour.co.in/${userName}`;
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);

      // Update user document
      await updateDoc(userRef, {
        userName,
        profileUrl: newProfileUrl
      });
      console.log('Username updated in users collection');

      // Update all associated tours
      if (userData.tourIds && userData.tourIds.length > 0) {
        console.log('Updating username in tours:', userData.tourIds);
        
        for (const tourId of userData.tourIds) {
          const tourRef = doc(db, 'Tours', tourId);
          const tourDoc = await getDoc(tourRef);

          if (tourDoc.exists()) {
            const tourData = tourDoc.data();
            let needsUpdate = false;
            let updatedTourData = { ...tourData };

            // Helper function to update username in arrays
            const updateUsernameInArray = (array) => {
              if (!array) return array;
              return array.map(item => {
                if (item.userId === userData.userId) {
                  needsUpdate = true;
                  return { 
                    ...item, 
                    userName,
                    profileUrl: newProfileUrl
                  };
                }
                return item;
              });
            };

            // Update all relevant arrays based on user type
            if (userData.userType === 'Artist' && updatedTourData.artistDetails) {
              updatedTourData.artistDetails = updateUsernameInArray(updatedTourData.artistDetails);
            }
            if (userData.userType === 'Crew' && updatedTourData.crewDetails) {
              updatedTourData.crewDetails = updateUsernameInArray(updatedTourData.crewDetails);
            }
            if (userData.userType === 'Vendor' && updatedTourData.vendorDetails) {
              updatedTourData.vendorDetails = updateUsernameInArray(updatedTourData.vendorDetails);
            }
            if (userData.userType === 'Staff' && updatedTourData.staffDetails) {
              updatedTourData.staffDetails = updateUsernameInArray(updatedTourData.staffDetails);
            }

            // Update createdBy if user is manager
            if (userData.userType === 'Manager' && 
                tourData.createdBy?.userId === userData.userId) {
              needsUpdate = true;
              updatedTourData.createdBy = {
                ...tourData.createdBy,
                userName,
                profileUrl: newProfileUrl
              };
            }

            if (needsUpdate) {
              console.log('Updating tour:', tourId);
              await updateDoc(tourRef, updatedTourData);
              if (updateTourData) {
                await updateTourData(tourId);
              }
            }
          }
        }
      }

      // Update context
      setUserData(prev => ({
        ...prev,
        userName,
        profileUrl: newProfileUrl
      }));

      setShowUsernameDialog(false);
      showCustomAlert({
        title: 'Success',
        message: 'Username updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating username:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to update username. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const shouldShowAssociatedActs = () => {
    if (!userData) return false;
    return ['Artist', 'Crew', 'Manager'].includes(userData.userType);
  };
  
  // Helper function to determine if user type should show endorsements
  const shouldShowEndorsements = () => {
    if (!userData) return false;
    return ['Artist', 'Crew', 'Manager'].includes(userData.userType);
  };

  const shouldShowManagedBy = () => {
    if (!userData) return false;
    return userData.userType === 'Artist';
  };
  
  // Add a handler for adding managers
  const handleManagedByAdd = async (manager) => {
    if (!manager || !manager.id) {
      showCustomAlert({
        title: 'Error',
        message: 'Invalid manager data provided',
        type: 'error'
      });
      return;
    }
  
    try {
      setIsLoading(true);
      
      // The Firestore update is handled by the ManagedBySection component
      // We only need to update local state
      
      setManagedBy(prev => {
        // Make sure we don't add duplicates
        if (prev.some(m => m.id === manager.id)) {
          return prev;
        }
        return [...prev, manager];
      });
      
      showCustomAlert({
        title: 'Success',
        message: 'Manager added successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error adding manager:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to add manager. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrandSearch = async () => {
    if (brandSearchTerm.trim()) {
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        let allResults = [];
        
        // Case insensitive search
        const searchTerm = brandSearchTerm.toLowerCase().trim();
        
        // Process both chunks with a generic approach
        const chunks = [
          { id: 'brands_chunk1', fieldName: 'data' },
          { id: 'brands_chunk2', fieldName: 'brandsDetails' }
        ];
        
        for (const chunk of chunks) {
          try {
            const docRef = doc(db, 'musicalBrands', chunk.id);
            const docSnapshot = await getDoc(docRef);
            
            if (docSnapshot.exists()) {
              const docData = docSnapshot.data();
              const brandsArray = docData[chunk.fieldName] || [];
              
              const results = brandsArray.filter(brand => 
                brand.brandName && brand.brandName.toLowerCase().includes(searchTerm)
              );
              
              allResults = [...allResults, ...results];
            }
          } catch (error) {
            console.error(`Error processing ${chunk.id}:`, error);
          }
        }
        
        // Sort and limit results
        allResults.sort((a, b) => a.brandName.localeCompare(b.brandName));
        setBrandSearchResults(allResults.slice(0, 10));
        setHasSearched(true);
      } catch (error) {
        console.error('Error searching brands:', error);
        showCustomAlert({
          title: 'Error',
          message: 'Failed to search brands. Please try again.',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };
  

  const handleBrandSelection = (brand) => {
    // Check if brand is already selected by comparing brandNames case-insensitively
    const isAlreadySelected = selectedBrands.some(b => 
      b.brandName.toLowerCase() === brand.brandName.toLowerCase()
    );
    
    if (!isAlreadySelected) {
      setSelectedBrands(prev => [...prev, brand]);
    }

    setShowBrandSearchModal(false);
  setBrandSearchTerm('');
  setBrandSearchResults([]);
  setHasSearched(false);

  };

  const removeSelectedBrand = (brandName) => {
  setSelectedBrands(prev => prev.filter(b => b.brandName !== brandName));
};


  // Add these handlers for associated acts
  const handleAssociatedActAdd = async (act) => {
    if (!act || !act.id || !act.name) {
      showCustomAlert({
        title: 'Error',
        message: 'Invalid act data provided',
        type: 'error'
      });
      return;
    }
  
    try {
      setIsLoading(true);
      
      // The actual Firestore update is now handled by AssociatedActsSection component
      // We only need to update local state and send notifications if needed
      
      // If it's a band and has adminIds, create notification
      if (act.type === 'band' && Array.isArray(act.adminIds)) {
        const db = getFirestoreInstance();
        await createBandNotification({
          bandId: act.id,
          type: NOTIFICATION_TYPES.USER_ADDED,
          title: 'New Association',
          message: `${userData.fullName} has added ${act.name} to their associated acts`,
          recipientIds: act.adminIds
        });
      }
  
      // Update local state with the new data coming from the AssociatedActsSection component
      // which already includes all necessary fields
      setAssociatedActs(prev => {
        // Make sure we don't add duplicates based on associationId
        if (act.associationId && prev.some(a => a.associationId === act.associationId)) {
          return prev;
        }
        return [...prev, act];
      });
      
      // Note: We don't need to update UserContext here as the AssociatedActsSection
      // already updates the context with the latest data
      
      showCustomAlert({
        title: 'Success',
        message: 'Associated act added successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error adding associated act:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to add associated act. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
 

  const handleProfilePictureClick = () => {
    profilePictureRef.current.click();
  };

  const handleCoverPhotoClick = () => {
    coverPhotoRef.current.click();
  };

  const handleProfilePictureChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePictureFile(file);
      setProfilePictureUrl(URL.createObjectURL(file));
    }
  };



  const uploadImageFile = async (file, path) => {
    if (!file) return null;

    try {
      const storage = getStorage();
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const handleUpdateProfile = async () => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
  
      let updatedProfilePictureUrl = profilePictureUrl;
      let updatedCoverPhotoUrl = profileCoverPictureUrl;
  
      // Upload new profile picture if changed
      if (profilePictureFile) {
        const path = `users/${userData.userId}/profileMedia/${Date.now()}_${profilePictureFile.name}`;
        updatedProfilePictureUrl = await uploadImageFile(profilePictureFile, path);
      }
  
      // Upload new cover photo if changed
      if (coverPhotoFile) {
        const path = `users/${userData.userId}/profileMedia/${Date.now()}_${coverPhotoFile.name}`;
        updatedCoverPhotoUrl = await uploadImageFile(coverPhotoFile, path);
      }
  
      // Prepare update data for main document
      const updateData = {
        fullName,
        bio,
        city,
        state,
        country,
        socialLinks,
        profilePictureUrl: updatedProfilePictureUrl,
        profileCoverPictureUrl: updatedCoverPhotoUrl,
        // Include userName if it has changed
        ...(userName !== userData.userName ? { userName } : {}),
        startedLearningYear,
        startedProfessionalYear,
        overallExperienceYears,
        professionalExperienceYears
      };
  
      // Update user document
      await updateDoc(userRef, updateData);
  
      // Get user's assigned events from assignedEvents subcollection
      if (userData.userId) {
        const assignedEventsRef = collection(db, 'users', userData.userId, 'assignedEvents');
        const assignedEventsSnapshot = await getDocs(assignedEventsRef);
        
        // Process each assigned event
        for (const assignedDoc of assignedEventsSnapshot.docs) {
          const assignedData = assignedDoc.data();
          const eventType = assignedData.type || 'tour'; // Default to tour if not specified
          const eventId = eventType === 'tour' ? assignedData.tourId : assignedData.showId;
          
          if (!eventId) continue;
          
          // Determine the collection based on event type
          const collectionName = eventType === 'tour' ? 'tours' : 'shows';
          
          try {
            // First check if the event exists
            const eventRef = doc(db, collectionName, eventId);
            const eventDoc = await getDoc(eventRef);
            
            if (!eventDoc.exists()) {
              console.log(`${eventType} ${eventId} not found, skipping update`);
              continue;
            }
            
            // Update user in assignedUsers subcollection
            const userAssignmentRef = doc(db, collectionName, eventId, 'assignedUsers', userData.userId);
            const userAssignmentDoc = await getDoc(userAssignmentRef);
            
            if (userAssignmentDoc.exists()) {
              // Update existing document in assignedUsers
              await updateDoc(userAssignmentRef, {
                fullName,
                profilePictureUrl: updatedProfilePictureUrl,
                userName: userName,
                userSubType: userData.userSubType || null,
                email: userData.email || null,
                updatedAt: serverTimestamp()
              });
              console.log(`Updated user in ${eventType} ${eventId} assignedUsers`);
            }
            
            // Additionally, if user is the creator, update the createdBy field in the main document
            const eventData = eventDoc.data();
            if (eventData.createdBy?.userId === userData.userId) {
              await updateDoc(eventRef, {
                'createdBy.fullName': fullName,
                'createdBy.profilePictureUrl': updatedProfilePictureUrl,
                'createdBy.userName': userName,
                'createdBy.userSubType': userData.userSubType || null,
                'createdBy.email': userData.email || null
              });
              console.log(`Updated createdBy in ${eventType} ${eventId}`);
              
              // Update TourContext data if available
              if (eventType === 'tour' && typeof updateTourData === 'function') {
                await updateTourData(eventId);
              }
            }
          } catch (error) {
            console.error(`Error updating ${eventType} ${eventId}:`, error);
            // Continue with other events even if one fails
          }
        }
      }
  
      // Update UserContext
      setUserData(prev => ({
        ...prev,
        ...updateData
      }));
  
      showCustomAlert({
        title: 'Success',
        message: 'Profile updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to update profile. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const AlertModal = () => {
    const getAlertIcon = () => {
      switch (alertConfig.type) {
        case 'success':
          return faCheckCircle;
        case 'error':
          return faTimesCircle;
        case 'warning':
          return faExclamationTriangle;
        default:
          return faInfoCircle;
      }
    };
  
    return (
      <div className={styles.alertModalOverlay}>
        <div className={styles.alertModal}>
          <div className={styles.alertHeader}>
            <FontAwesomeIcon 
              icon={getAlertIcon()} 
              className={`${styles.alertIcon} ${styles[alertConfig.type]}`}
            />
            <h3 className={styles.alertTitle}>{alertConfig.title}</h3>
          </div>
          <p className={styles.alertMessage}>{alertConfig.message}</p>
          <div className={styles.alertActions}>
            {alertConfig.showCancel && (
              <button 
                className={`${styles.alertButton} ${styles.cancel}`}
                onClick={() => setShowAlert(false)}
              >
                Cancel
              </button>
            )}
            <button 
              className={`${styles.alertButton} ${styles[alertConfig.type]}`}
              onClick={() => {
                if (alertConfig.onConfirm) alertConfig.onConfirm();
                setShowAlert(false);
              }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LoadingSpinner = () => (
    <div className={styles.loadingOverlay}>
      <div className={styles.spinner}></div>
    </div>
  );

  return (
    <div className={styles.mainLayout}>
      <TopBar />
      <LeftMenu />
      <div className={styles.editProfileMain}>
        <div className={styles.editProfileContent}>
          <div className={styles.editProfileHeader}>
            <h1 className={styles.title}>Edit Profile</h1>
          </div>

          <div className={styles.formContainer}>
            {/* Profile Pictures Section */}
            <div className={styles.picturesSection}>
              <div className={styles.coverPhotoContainer}>
                <img 
                  src={profileCoverPictureUrl || defaultCoverPhoto}
                  alt="Cover"
                  className={styles.coverPhoto}
                />
                <button 
                  className={styles.editCoverPhotoButton}
                  onClick={handleCoverPhotoClick}
                >
                  <FontAwesomeIcon icon={faCamera} />
                </button>
                <input
                  type="file"
                  accept="image/*"
                  ref={coverPhotoRef}
                  onChange={handleCoverPhotoChange}
                  className={styles.hiddenInput}
                />
              </div>

              <div className={styles.profilePictureContainer}>
                <img 
                  src={profilePictureUrl || defaultProfilePic}
                  alt="Profile"
                  className={styles.profilePicture}
                />
                <button 
                  className={styles.editProfilePictureButton}
                  onClick={handleProfilePictureClick}
                >
                  <FontAwesomeIcon icon={faCamera} />
                </button>
                <input
                  type="file"
                  accept="image/*"
                  ref={profilePictureRef}
                  onChange={handleProfilePicChange}
                  className={styles.hiddenInput}
                />
              </div>
            </div>

            {/* Basic Information */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Basic Information</h2>
              
              <div className={styles.field}>
                <label>Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={styles.input}
                  placeholder="Your full name"
                />
              </div>

              <div className={styles.field}>
                <label>Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className={styles.textarea}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  maxLength={500}
                />
                <div className={styles.charCount}>
                  {bio.length}/500 characters
                </div>
              </div>

              <div className={styles.field}>
                <label>Username</label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className={styles.input}
                    placeholder="username"
                  />
                  <button 
                    onClick={() => setShowUsernameDialog(true)}
                    className={styles.updateButton}
                  >
                    Update
                  </button>
                </div>
                <div className={styles.helperText}>
                  Only lowercase letters, numbers, and underscore (_) are allowed. No spaces.
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Location</h2>
              
              <div className={styles.field}>
                <label>City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className={styles.input}
                  placeholder="Your city"
                />
              </div>

              <div className={styles.field}>
                <label>State</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className={styles.input}
                  placeholder="Your state"
                />
              </div>

              <div className={styles.field}>
                <label>Country</label>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className={styles.input}
                  placeholder="Your country"
                />
              </div>
            </div>

            {userData?.userSubType && (
  <div className={styles.section}>
    <h2 className={styles.sectionTitle}>Experience</h2>
    
    <div className={styles.field}>
      <label>
        When did you first start {getActivityVerb(userData.userSubType)}?
      </label>
      <select
        value={startedLearningYear}
        onChange={(e) => setStartedLearningYear(e.target.value)}
        className={styles.select}
      >
        <option value="">Select Year</option>
        {generateYearOptions().map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {overallExperienceYears !== null && (
        <div className={styles.helperText}>
          Overall years: {overallExperienceYears} {overallExperienceYears === 1 ? 'year' : 'years'}
        </div>
      )}
    </div>

    <div className={styles.field}>
      <label>
        When did you start {getActivityVerb(userData.userSubType)} professionally?
      </label>
      <select
        value={startedProfessionalYear}
        onChange={(e) => setStartedProfessionalYear(e.target.value)}
        className={styles.select}
      >
        <option value="">Select Year</option>
        {generateYearOptions().map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {professionalExperienceYears !== null && (
        <div className={styles.helperText}>
          Professional experience: {professionalExperienceYears} {professionalExperienceYears === 1 ? 'year' : 'years'}
        </div>
      )}
    </div>
  </div>
)}

            {/* Social Links */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Social Links</h2>
              
              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faInstagram} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={socialLinks.instagramUrl}
                    onChange={(e) => handleSocialLinkChange('instagramUrl', e.target.value)}
                    className={styles.input}
                    placeholder="Instagram URL"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faTwitter} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={socialLinks.twitterUrl}
                    onChange={(e) => handleSocialLinkChange('twitterUrl', e.target.value)}
                    className={styles.input}
                    placeholder="Twitter URL"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faFacebook} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={socialLinks.facebookUrl}
                    onChange={(e) => handleSocialLinkChange('facebookUrl', e.target.value)}
                    className={styles.input}
                    placeholder="Facebook URL"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faYoutube} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={socialLinks.youtubeUrl}
                    onChange={(e) => handleSocialLinkChange('youtubeUrl', e.target.value)}
                    className={styles.input}
                    placeholder="YouTube URL"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faLinkedin} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={socialLinks.linkedInUrl}
                    onChange={(e) => handleSocialLinkChange('linkedInUrl', e.target.value)}
                    className={styles.input}
                    placeholder="LinkedIn URL"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <div className={styles.socialIconContainer}>
                    <img src={spotifyLogo} alt="Spotify" className={styles.brandSocialIcon} />
                  </div>
                  <input
                    type="url"
                    value={socialLinks.spotifyUrl}
                    onChange={(e) => handleSocialLinkChange('spotifyUrl', e.target.value)}
                    className={styles.input}
                    placeholder="Spotify URL"
                  />
                </div>
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <div className={styles.socialIconContainer}>
                    <img src={appleMusicLogo} alt="Apple Music" className={styles.brandSocialIcon} />
                  </div>
                  <input
                    type="url"
                    value={socialLinks.appleMusicUrl}
                    onChange={(e) => handleSocialLinkChange('appleMusicUrl', e.target.value)}
                    className={styles.input}
                    placeholder="Apple Music URL"
                  />
                </div>
              </div>
            </div>

       

  {/* Associated Acts Section - Conditionally rendered based on user type */}
  {shouldShowAssociatedActs() && (
    <AssociatedActsSection 
                associatedActs={associatedActs}
                pastAssociatedActs={userData?.pastAssociatedActs || []}
                onAssociatedActAdd={handleAssociatedActAdd}
                onBandUpdate={() => {
                  // Refresh user data
                  if (userData.userId) {
                    const db = getFirestoreInstance();
                    getDoc(doc(db, 'users', userData.userId)).then(userDoc => {
                      if (userDoc.exists()) {
                        const updatedUserData = userDoc.data();
                        setUserData(prev => ({
                          ...prev,
                          ...updatedUserData
                        }));
                        setAssociatedActs(updatedUserData.associatedActs || []);
                      }
                    });
                  }
                }}
                userData={userData}
                showAlert={showCustomAlert}
              />
            )}

            {/* Endorsements Section - Conditionally rendered based on user type */}
            {shouldShowEndorsements() && (
              <EndorsementsSection
                userData={userData}
                showCustomAlert={showCustomAlert}
              />
            )}

            {/* Managed By Section - Conditionally rendered for Artist users */}
{shouldShowManagedBy() && (
  <ManagedBySection 
    managedBy={managedBy}
    pastManagedBy={userData?.pastManagedBy || []}
    onManagedByAdd={handleManagedByAdd}
    onBandUpdate={() => {
      // Refresh user data
      if (userData.userId) {
        const db = getFirestoreInstance();
        getDoc(doc(db, 'users', userData.userId)).then(userDoc => {
          if (userDoc.exists()) {
            const updatedUserData = userDoc.data();
            setUserData(prev => ({
              ...prev,
              ...updatedUserData
            }));
            setManagedBy(updatedUserData.managedBy || []);
          }
        });
      }
    }}
    userData={userData}
    showAlert={showCustomAlert}
  />
)}

            {/* Save Button */}
            <button 
              onClick={handleUpdateProfile}
              className={styles.saveButton}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>
      <RightMenu />

      {/* Username Update Confirmation Dialog */}
      {showUsernameDialog && (
        <div className={styles.alertModalOverlay}>
          <div className={styles.alertModal}>
            <div className={styles.alertHeader}>
              <FontAwesomeIcon 
                icon={faExclamationTriangle} 
                className={styles.alertIcon} 
              />
              <h3 className={styles.alertTitle}>Update Username</h3>
            </div>
            <p className={styles.alertMessage}>
              Are you sure you want to update your username? This will update your username across all your tours.
            </p>
            <div className={styles.alertActions}>
              <button 
                className={`${styles.alertButton} ${styles.cancel}`}
                onClick={() => setShowUsernameDialog(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className={`${styles.alertButton} ${styles.confirm}`}
                onClick={handleUpdateUsername}
                disabled={isLoading}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {showProfileCropper && (
  <ImageCropper
    image={selectedImage.preview}
    onCropComplete={handleCropComplete}
    onCancel={() => {
      setShowProfileCropper(false);
      setSelectedImage(null);
    }}
    aspect={1}
    cropShape="round"
    minWidth={500}
  />
)}

{showCoverCropper && (
  <ImageCropper
    image={selectedImage.preview}
    onCropComplete={handleCropComplete}
    onCancel={() => {
      setShowCoverCropper(false);
      setSelectedImage(null);
    }}
    aspect={16/9}
    minWidth={1200}
    minHeight={675}
  />
)}

      {/* Brand Search Modal */}
      {showBrandSearchModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Search Brands</h2>
              <button 
                className={styles.closeButton}
                onClick={() => {
                  setShowBrandSearchModal(false);
                  setBrandSearchTerm('');
                  setBrandSearchResults([]);
                  setHasSearched(false);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder="Search brands..."
                value={brandSearchTerm}
                onChange={(e) => setBrandSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBrandSearch()}
              />
              <button 
                className={styles.searchButton}
                onClick={handleBrandSearch}
                disabled={!brandSearchTerm.trim()}
              >
                <FontAwesomeIcon icon={faSearch} />
                Search
              </button>
            </div>

            <div className={styles.searchResults}>
              {isLoading ? (
                <div className={styles.loading}>Loading...</div>
              ) : hasSearched ? (
                brandSearchResults.length > 0 ? (
                  <div className={styles.resultsGrid}>
    {brandSearchResults.map(brand => (
      <div 
        key={brand.brandName} 
        className={styles.resultCard}
        onClick={() => handleBrandSelection(brand)}
      >
        <div className={styles.brandResultContent}>
          <img 
            src={brand.logo || defaultProfilePic} 
            alt={brand.brandName}
            className={styles.brandLogo}
          />
          <div className={styles.brandInfo}>
            <h3>{brand.brandName}</h3>
            {brand.website && (
              <div className={styles.brandWebsite}>
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                <span>{brand.website}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
                ) : (
                  <div className={styles.noResults}>
                    No brands found for "{brandSearchTerm}"
                  </div>
                )
              ) : (
                <div className={styles.searchPrompt}>
                  Search for musical brands you endorse
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Brand website viewing will be handled in Profile.js */}

      {/* Custom Alert Dialog */}
      {showAlert && <AlertModal />}
      
      {/* Loading Spinner */}
      {isLoading && <LoadingSpinner />}
    </div>
  );
};

export default EditProfile;